import { useId, type ComponentType } from 'react';
import type { StartMode } from '../../stitch-landing.js';

/**
 * Starter illustrations are monochrome ink drawings on paper, drawn from the neutral theme roles:
 * weight and density carry the idea, not color.
 */
const token = (role: string) => `var(--md-sys-color-${role})`;
const PAPER = token('surface-container-lowest'), INK = token('on-surface'), PENCIL = token('outline'), GUIDE = token('outline-variant');

const W = 320, H = 200;

/** React ids may carry characters that break `url(#…)` references. */
const useSvgId = () => useId().replace(/[^\w-]/g, '');

function Paper({ dots = false }: { dots?: boolean }) {
  const id = useSvgId();
  return (
    <>
      <rect width={W} height={H} fill={PAPER} />
      {dots && (
        <>
          <defs>
            <pattern id={id} width="14" height="14" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1" fill={GUIDE} /></pattern>
          </defs>
          <rect width={W} height={H} fill={`url(#${id})`} />
        </>
      )}
    </>
  );
}

function BlankArt() {
  return (
    <>
      <Paper dots />
      <rect x="116" y="68" width="88" height="64" rx="12" fill={PAPER} stroke={PENCIL} strokeWidth="1.2" strokeDasharray="4 4" />
      <path d="M160 88v24M148 100h24" stroke={INK} strokeWidth="2" strokeLinecap="round" />
    </>
  );
}

/** A south facade whose fins thicken toward the sun. */
function FacadeArt() {
  const fins = Array.from({ length: 22 }, (_, index) => ({ x: 62 + index * 9, width: 0.8 + 4.6 * (index / 21) ** 1.4 }));
  return (
    <>
      <Paper />
      <circle cx="276" cy="36" r="12" fill="none" stroke={PENCIL} strokeWidth="1.2" />
      {[0, 1, 2].map(ray => <path key={ray} d={`M${256 - ray * 10} ${54 + ray * 8}l-${14 + ray * 4} ${10 + ray * 4}`} stroke={GUIDE} strokeWidth="1" strokeLinecap="round" />)}
      <rect x="52" y="52" width="208" height="124" fill="none" stroke={PENCIL} strokeWidth="1" />
      {[83, 114, 145].map(y => <path key={y} d={`M52 ${y}h208`} stroke={GUIDE} />)}
      {fins.map(fin => <rect key={fin.x} x={fin.x - fin.width / 2} y="52" width={fin.width} height="124" fill={INK} opacity={0.35 + 0.55 * (fin.width / 5.4)} />)}
      <path d="M24 176h272" stroke={INK} strokeWidth="1.4" />
    </>
  );
}

/** A hyperbolic paraboloid roof (z = uv: two corners up, two down) standing on four columns. */
const hypar = (() => {
  const point = (u: number, v: number) => {
    const z = 0.5 * u * v;
    return `${(160 + (u - v) * 72).toFixed(1)} ${(108 + (u + v) * 14 - z * 64).toFixed(1)}`;
  };
  const steps = Array.from({ length: 11 }, (_, index) => -1 + index / 5);
  const line = (at: (t: number) => string) => `M${steps.map(at).join('L')}`;
  const mesh = steps.flatMap(fixed => [line(t => point(fixed, t)), line(t => point(t, fixed))]);
  const columns = ([[1, 1], [1, -1], [-1, 1], [-1, -1]] as const).map(([u, v]) => {
    const [x, y] = point(u, v).split(' ').map(Number) as [number, number];
    return `M${x} ${y}L${x} ${108 + (u + v) * 14 + 52}`;
  });
  return { mesh, columns, edge: [line(t => point(t, -1)), line(t => point(1, t)), line(t => point(-t, 1)), line(t => point(-1, -t))] };
})();

function RoofArt() {
  return (
    <>
      <Paper />
      <path d="M24 170h272" stroke={GUIDE} />
      {hypar.columns.map(d => <path key={d} d={d} stroke={PENCIL} strokeWidth="1.4" />)}
      {hypar.mesh.map(d => <path key={d} d={d} fill="none" stroke={PENCIL} strokeWidth="0.6" />)}
      {hypar.edge.map(d => <path key={d} d={d} fill="none" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />)}
    </>
  );
}

/** A Pratt truss whose member weight follows its stress: chords peak at midspan, diagonals at the supports. */
const truss = (() => {
  const panels = 8, left = 40, span = 240, top = 72, bottom = 136, step = span / panels;
  const members: Array<{ d: string; stress: number }> = [];
  const mid = left + span / 2;
  const chordStress = (x: number) => 1 - Math.abs(x - mid) / (span / 2);
  for (let index = 0; index < panels; index++) {
    const x0 = left + index * step, x1 = x0 + step, center = (x0 + x1) / 2;
    members.push({ d: `M${x0} ${bottom}H${x1}`, stress: chordStress(center) });
    members.push({ d: `M${x0} ${top}H${x1}`, stress: chordStress(center) * 0.9 });
    const diagonal = x1 <= mid ? `M${x0} ${top}L${x1} ${bottom}` : `M${x0} ${bottom}L${x1} ${top}`;
    members.push({ d: diagonal, stress: 1 - chordStress(center) });
  }
  for (let index = 0; index <= panels; index++) members.push({ d: `M${left + index * step} ${top}V${bottom}`, stress: 0.1 });
  return { members, left, right: left + span, top, bottom, loads: Array.from({ length: panels - 1 }, (_, index) => left + (index + 1) * step) };
})();

function TrussArt() {
  return (
    <>
      <Paper dots />
      {truss.loads.map(x => <path key={x} d={`M${x} ${truss.top - 28}v20m-3 -5l3 5l3 -5`} fill="none" stroke={PENCIL} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />)}
      {truss.members.map(member => (
        <path key={member.d} d={member.d} strokeLinecap="round" stroke={member.stress > 0.5 ? INK : PENCIL} strokeWidth={0.8 + 3 * member.stress} />
      ))}
      {[truss.left, truss.right].map(x => <path key={x} d={`M${x} ${truss.bottom}l-8 13h16z`} fill="none" stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />)}
    </>
  );
}

const ART: Record<StartMode | 'blank', ComponentType> = { blank: BlankArt, design: FacadeArt, parametric: RoofArt, analysis: TrussArt };

export function StarterArt({ kind, className }: { kind: StartMode | 'blank'; className?: string }) {
  const Art = ART[kind];
  return (
    <svg aria-hidden viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className={className}>
      <Art />
    </svg>
  );
}
